import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { Role } from '@novaheal/types';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import { toastContent } from 'config/toast';
import { SuccessStatusType } from 'types/StatusTypes';
import { ToastVariant } from 'types/Toast';
import { PublicRoutes } from 'types/Routes';
import storage from 'utils/storage';
import { useUser } from 'utils/auth';

/**
 * Admin impersonation hooks
 *
 * Two hooks serve the same feature but different mount scopes:
 *
 * - {@link useAdminImpersonationActivation} — URL token, PIN modal, route cleanup. Mount **once** in `AppRoutes`.
 * - {@link useAdminImpersonationSession} — banner state + exit session. Mount in layout chrome (`AdminBar`).
 *
 * Both read/write `storage` admin token keys. Axios prefers the admin token when present
 * (see `utils/axios.tsx`). `useAdminImpersonationSession` listens to `adminTokenChangedEvent` for UI sync.
 */

export type UseAdminImpersonationActivationResult = {
  /** One-time activation token from `?adminToken=`; drives AdminPinModal. */
  pendingAdminToken: string | null;
  /** Called after PIN validation; stores impersonation JWT and refetches `/user/me`. */
  handlePinSuccess: (accessToken: string) => Promise<void>;
  /** Dismisses the PIN modal without activating a session. */
  handlePinClose: () => void;
};

const publicRoutePaths = new Set<string>(Object.values(PublicRoutes));

const isPublicRoute = (pathname: string): boolean => publicRoutePaths.has(pathname);

const ADMIN_IMPERSONATION_ALLOWED_ROLES: Role[] = [Role.SUPPORT, Role.ADMIN, Role.SALES];

/**
 * Admin impersonation activation flow for the app shell.
 *
 * **Mount once** in `AppRoutes` (pairs with `AdminPinModal`).
 *
 * Responsibilities:
 * - Parse `?adminToken=` and open the PIN modal
 * - Handle `?sessionExpired=` toast
 * - Clear admin session when navigating to {@link PublicRoutes}
 *
 * Active session UI lives in {@link useAdminImpersonationSession} — not here.
 *
 * @remarks
 * Effects depend on `refetch` from `useUser()`, not the whole query result object.
 * Including the full `UseQueryResult` in dependency arrays causes infinite refetch loops.
 */
export const useAdminImpersonationActivation = (): UseAdminImpersonationActivationResult => {
  const toast = useNovaToast();
  const { data: userData, refetch } = useUser();
  const [params, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const isActivatingAdminSessionRef = useRef(false);
  const [pendingAdminToken, setPendingAdminToken] = useState<string | null>(null);
  useEffect(() => {
    if (!isPublicRoute(pathname)) {
      return;
    }

    if (!storage.getAdminToken()) {
      return;
    }

    storage.clearAdminSession();
    refetch().catch(() => undefined);
  }, [pathname, refetch]);

  useEffect(() => {
    const adminSessionExpired = params.get('sessionExpired');

    if (!adminSessionExpired) {
      return;
    }

    toast.show({
      variant: ToastVariant.ERROR,
      title: 'Admin Session Expired',
      description: 'Your admin session has expired. Please log in again.',
    });

    const next = new URLSearchParams(params);
    next.delete('sessionExpired');
    setSearchParams(next, { replace: true });
  }, [params, setSearchParams, toast]);

  useEffect(() => {
    const adminToken = params.get('adminToken');
    const hasExistingAdminSession = storage.getAdminToken() !== null;

    if (!adminToken) {
      return;
    }

    if (!userData?.role || !ADMIN_IMPERSONATION_ALLOWED_ROLES.includes(userData?.role)) {
      return;
    }

    if (isActivatingAdminSessionRef.current) {
      return;
    }

    isActivatingAdminSessionRef.current = true;

    let isCancelled = false;

    const activateAdminSession = async (): Promise<void> => {
      try {
        if (hasExistingAdminSession) {
          storage.clearAdminSession();
          await refetch();
        }

        if (isCancelled) {
          return;
        }

        const next = new URLSearchParams(params);
        next.delete('adminToken');
        setSearchParams(next, { replace: true });
        setPendingAdminToken(adminToken);
      } catch {
        // Keep the hook resilient if refetch or navigation side effects fail.
      } finally {
        isActivatingAdminSessionRef.current = false;
      }
    };

    activateAdminSession();

    return () => {
      isCancelled = true;
    };
  }, [params, setSearchParams, refetch, userData?.role]);

  const handlePinSuccess = async (accessToken: string): Promise<void> => {
    setPendingAdminToken(null);
    storage.setAdminToken(accessToken);

    if (userData?.role !== Role.USER) {
      toast.show({
        ...toastContent.success[SuccessStatusType.DEFAULT],
      });
    }

    const { data: refetchedUser } = await refetch();

    storage.setAdminTenant(refetchedUser?.b2bSchoolId || '');
  };

  const handlePinClose = (): void => {
    setPendingAdminToken(null);
  };

  return {
    pendingAdminToken,
    handlePinSuccess,
    handlePinClose,
  };
};
